// KÖZÖS melléklet-feltöltő logika — a kliens-validáció (darab/típus/méret), a SORBAN feltöltés (a hibás fájl
// megszakítja a láncot) és a hibafajta → toast leképezés. A közös `AttachmentPanel` és a portál jegy-lapja (Évi #82)
// is ezt használja; a felület (gomb, lista) a hívóé.
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { type AttachmentUploadOutcome, validateAttachments } from '@/lib/attachment-rules';

/** A feltöltés FELOLDOTT toast-üzenetei. */
export interface AttachmentUploaderMessages {
  limit: string;
  invalidType: string;
  tooLarge: string;
  uploaded: string;
  uploadFailed: string;
  forbidden: string;
  validation: string;
  unavailable: string;
}

export interface AttachmentUploader {
  busy: boolean;
  /** A rejtett `<input type="file">` ref-je (a gomb ezt nyitja; feltöltés után ürül, a fájl újraválasztható). */
  inputRef: React.RefObject<HTMLInputElement>;
  onFiles: (files: FileList) => Promise<void>;
}

export function useAttachmentUploader(opts: {
  /** A hordozón MÁR meglévő mellékletek száma (a darab-plafonhoz). */
  existingCount: number;
  onUpload: (file: File) => Promise<AttachmentUploadOutcome>;
  /** Legalább egy sikeres feltöltés után — a hívó invalidálja a saját cache-kulcsait. */
  onChanged: () => void;
  messages: AttachmentUploaderMessages;
}): AttachmentUploader {
  const { existingCount, onUpload, onChanged, messages } = opts;
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  /** Egy fájl feltöltése; a hiba FAJTÁJÁT itt fordítjuk felhasználói üzenetre. */
  const uploadOne = async (file: File): Promise<boolean> => {
    const outcome = await onUpload(file);
    if (outcome === 'ok') return true;
    if (outcome === 'validation') toast.error(messages.validation);
    else if (outcome === 'forbidden') toast.error(messages.forbidden);
    else if (outcome === 'unavailable') toast.error(messages.unavailable);
    else toast.error(messages.uploadFailed);
    return false;
  };

  const onFiles = async (files: FileList): Promise<void> => {
    const picked = Array.from(files);
    if (picked.length === 0) return;

    // Kliens-oldali validáció (UX) — a szerver a forrás-igazság.
    const invalid = validateAttachments(picked, existingCount);
    if (invalid === 'limit') {
      toast.error(messages.limit);
      return;
    }
    if (invalid === 'type') {
      toast.error(messages.invalidType);
      return;
    }
    if (invalid === 'size') {
      toast.error(messages.tooLarge);
      return;
    }

    setBusy(true);
    try {
      let anySuccess = false;
      // Egymás után töltünk fel (determinisztikus sorrend; a hibás fájl megszakítja a láncot).
      for (const file of picked) {
        const ok = await uploadOne(file);
        if (!ok) break;
        anySuccess = true;
      }
      if (anySuccess) {
        toast.success(messages.uploaded);
        onChanged();
      }
    } catch {
      // a feltöltő váratlan kivétele sem szökhet ki — a felhasználó hiba-toastot kap
      toast.error(messages.uploadFailed);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return { busy, inputRef, onFiles };
}
